import {
  ButtonERP,
  DataTablePrimeReact,
  DropdownPrimeReact,
  PaginationERP,
  SearchInput,
  TableERP,
} from "@finanedi.es/storybook"
import { EntidadConciliableReadingDtoProps } from "api/DTOs/EntidadConciliableReadingDto"
import { ConciliablesQueryParamsProps } from "api/interfaces/ConciliablesQueryParams"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import { handleTableSelection } from "handlers/handleTableSelection"
import { useFilterMenu } from "hooks/useFilterMenu"
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { backendDateFormat } from "utils/formatDate"
import { TAB, TabItem } from "../constants/constants"
import { SelectedDocumentsProps } from "../layout/ColumnRight"
import { DrawerDocumentosSeleccionados } from "./DrawerDocumentosSeleccionados"
import PANEL_CONCILIABLES_COLUMNS from "./panelConciliablesColumns"

interface PanelConciliablesProps {
  importePendiente?: number
  conciliationId?: string | null
  handleCrearAsiento: () => void
  tabSelected: TabItem
  setTabSelected: Dispatch<SetStateAction<TabItem>>
  datePadre: [Date, Date | null]
  refrescar: number
  isLowerSectionExpanded: boolean
  selectedNewRight: Array<SelectedDocumentsProps>
  setIsLowerSectionExpanded: Dispatch<SetStateAction<boolean>>
  setSelectedNewRight: React.Dispatch<
    React.SetStateAction<Array<SelectedDocumentsProps>>
  >
  toggleLowerSection: () => void
}

const PanelConciliables: React.FC<PanelConciliablesProps> = ({
  importePendiente,
  conciliationId,
  handleCrearAsiento,
  tabSelected,
  setTabSelected,
  isLowerSectionExpanded,
  selectedNewRight,
  setSelectedNewRight,
  setIsLowerSectionExpanded,
  toggleLowerSection,
  datePadre,
  refrescar,
}) => {
  const { t } = useTranslation()
  const { id: paramId = "" } = useParams<Record<"id", string>>()
  const paginationSkipCount = useRef(0)
  const currentSkipCount = paginationSkipCount?.current
  const [keyword, setKeyword] = useState<string | undefined>()
  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })

  const dtRef = useRef<DataTablePrimeReact<
    EntidadConciliableReadingDtoProps[]
  > | null>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }

  const fetchConciliables = useCallback(
    () => (queryParams: ConciliablesQueryParamsProps) => {
      tabSelected.CANCEL_TOKEN?.cancel()
      if (!datePadre[1]) return
      return tabSelected.SERVICE({
        ...queryParams,
        ...(tabSelected.LABEL ===
          "bancos.conciliacion.modal.headerAccounting" && {
          CuentaBancariaId: paramId,
        }),
        ...(datePadre[1] && {
          FechaLowerLimit: backendDateFormat(datePadre[0]),
        }),
        ...(datePadre[1] && {
          FechaUpperLimit: backendDateFormat(
            datePadre[1]
              ? datePadre[1]
              : datePadre[0] <= new Date()
              ? new Date()
              : datePadre[0]
          ),
        }),
        ...(keyword && { Keyword: keyword }),
        SkipCount: pagination.skipCount,
        SortingCriteria: sortingCriteria?.sort
          ? (`${sortingCriteria.property} ${sortingCriteria.sort}` as SortingCriteria)
          : undefined,
        MaxResultCount: pagination.itemsPerPage,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      conciliationId,
      currentSkipCount,
      datePadre,
      importePendiente,
      keyword,
      tabSelected,
      pagination,
      sortingCriteria?.sort,
    ]
  )
  const { items, isLoading, totalCount, fetchTableData } = useFilterMenu({
    serviceCall: fetchConciliables,
  })

  useEffect(() => {
    handleResetPagination()
    fetchTableData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refrescar])

  const handleTabChange = (tabLabel: TabItem["LABEL"]) => {
    if (tabLabel === tabSelected.LABEL) return
    handleResetPagination()
    paginationSkipCount.current = 0
    return setTabSelected(
      Object.values(TAB).find((tab) => tab.LABEL === tabLabel) as TabItem
    )
  }
  useEffect(() => {
    const element = document.querySelector(".tabla-documentos-conciliables")
    if (element) {
      element.scrollTop = 0
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skipCount, pagination.itemsPerPage, tabSelected.KEY])

  const unSelectedRow = (rowData: EntidadConciliableReadingDtoProps) => {
    const selected = [...selectedNewRight]
    const index = selected.findIndex((item) => item.id === rowData.id)
    if (index !== -1) selected.splice(index, 1)
    setSelectedNewRight(selected)
    selected.length < 1 && setIsLowerSectionExpanded(false)
  }

  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)
  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
    handleResetPagination()
  }

  return (
    <section
      className='tw-flex tw-flex-col tw-text-nowrap tw-bg-background tw-h-full'
      id='section-documentos-conciliables'
    >
      <div className='tw-flex tw-flex-row tw-items-center tw-flex-none tw-justify-between tw-bg-white tw-px-6 tw-py-4 tw-mb-1 tw-shadow-3xl'>
        <div className='tw-flex tw-flex-row tw-w-fit tw-items-center tw-text-lg tw-font-medium tw-text-ellipsis tw-whitespace-nowrap'>
          <span className='tw-w-fit tw-whitespace-nowrap tw-mr-2 tw-text-ellipsis'>
            {t("bancos.conciliacion.modal.titleContabilidad")}
          </span>
        </div>

        <div className='tw-flex tw-flex-row tw-flex-wrap tw-w-auto tw-gap-3'>
          <SearchInput
            placeholder={`${t("bancos.conciliacion.searchPlaceholder")}`}
            onChange={(ev: string) => {
              setKeyword(ev)
              handleResetPagination()
            }}
            inputValue={keyword}
            className={`tw-h-10 tw-w-full sm:tw-w-72 ${
              tabSelected.KEY === TAB.PAGOS_COBROS.KEY ? "!tw-w-40" : ""
            }`}
          />
          {tabSelected.KEY === TAB.PAGOS_COBROS.KEY && (
            <ButtonERP
              customize={{
                type: "primary",
                size: "md",
              }}
              onClick={handleCrearAsiento}
              label={`${t("bancos.conciliacion.modal.labelCreateAsiento")}`}
            />
          )}

          <DropdownPrimeReact
            id='tabla-documentos-conciliables-dropdown'
            name='tabla-documentos-conciliables-dropdown'
            pt={{
              root: {
                className:
                  "tw-rounded-md !tw-w-44 tw-h-10 tw-border-neutral-25",
              },
              list: { className: "[&>*:first-child]:!tw-p-0" },
              itemGroupLabel: { className: "tw-text-secondary tw-text-sm" },
              wrapper: {
                className: "tw-rounded !tw-max-h-fit tw-h-fit",
              },
            }}
            value={tabSelected.LABEL}
            options={[
              { group: [TAB.PAGOS_COBROS.LABEL] },
              {
                group: Object.values(TAB)
                  .map((tab) => tab.LABEL)
                  .filter((label) => label !== TAB.PAGOS_COBROS.LABEL),
                label: "SUGERENCIAS",
              },
            ]}
            optionGroupLabel='label'
            optionGroupChildren='group'
            valueTemplate={(option) => (
              <span className='tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-0 tw-text-text'>
                {t(option)}
              </span>
            )}
            itemTemplate={(option) => t(option)}
            onChange={(e) => {
              handleTabChange(e.value)
            }}
          />
        </div>
      </div>
      <div className='tw-flex-grow tw-relative tw-h-[calc(100%-80px)]'>
        <div className='tw-flex tw-flex-col tw-h-[calc(100%-64px)] tw-relative'>
          <div className='tw-h-[calc(100%-60px)] tw-min-h-0'>
            <TableERP
              refTable={dtRef}
              columns={PANEL_CONCILIABLES_COLUMNS(t, tabSelected)}
              className='tw-h-full tw-max-w-full'
              columnResizeMode='expand'
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={onSort}
              removableSort
              value={items}
              showGridlines
              dataKey='id'
              lazy
              scrollable
              selection={selectedNewRight ?? []}
              selectionMode='checkbox'
              size='small'
              selectionColumnMode='multiple'
              onRowClick={({ data }) => {
                const selected = [...selectedNewRight]
                const index = selected.findIndex((item) => item.id === data.id)
                const dataWithTabKey = { ...data, tabKey: tabSelected.KEY }
                if (index === -1) selected.push(dataWithTabKey)
                else selected.splice(index, 1)
                setSelectedNewRight(selected)
              }}
              onSelectionChange={({
                value,
                type,
              }: {
                originalEvent: React.SyntheticEvent
                value: SelectedDocumentsProps[]
                type?: "multiple" | "all" | "checkbox" | "row" | undefined
              }) => {
                const { items: newSelection } = handleTableSelection({
                  currentSelection: selectedNewRight,
                  value,
                  type,
                  currentPageItems: items as SelectedDocumentsProps[],
                })

                const valueWithTabKey = newSelection.map((item) =>
                  item?.tabKey
                    ? item
                    : {
                        ...item,
                        tabKey: tabSelected.KEY,
                      }
                )
                setSelectedNewRight(valueWithTabKey)
              }}
              loading={isLoading}
              emptyMessage={`${t("primeReact.table.emptyMessage")}`}
            />
          </div>
          <PaginationERP
            className={PAGINATION_STYLES}
            totalRecords={totalCount}
            first={pagination.skipCount}
            rows={pagination.itemsPerPage}
            resetScroll={() => dtRef.current && dtRef.current?.resetScroll()}
            setPagination={setPagination}
            optionDropdown={[10, 20, 50, 100]}
            texts={{
              textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
              textCurrentPageReport: `${t("pagination.from")}`,
            }}
          />
        </div>
        <DrawerDocumentosSeleccionados
          isLowerSectionExpanded={isLowerSectionExpanded}
          selectedDocuments={selectedNewRight}
          toggleLowerSection={toggleLowerSection}
          unSelectedRow={unSelectedRow}
        />
      </div>
    </section>
  )
}

export default PanelConciliables
